import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus,
  Truck,
  Loader2,
  Search,
  Edit,
  Trash2,
  Phone,
  Mail,
  MapPin,
  User,
  Store,
  Copy
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPLIER_TYPES = [
  { value: 'shop', label: 'Shop Only' },
  { value: 'fuel', label: 'Fuel Station Only' },
  { value: 'both', label: 'Both Shop & Fuel' }
];

export const SuppliersPage = () => {
  const { isAdmin, isManager, isSuperUser } = useAuth();
  const { stores, selectedStore, setSelectedStore } = useStore();
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedStoreId, setSelectedStoreId] = useState('');
  
  // Copy suppliers state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [sourceStoreId, setSourceStoreId] = useState('');
  const [destStoreId, setDestStoreId] = useState('');
  const [sourceSuppliers, setSourceSuppliers] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [loadingSource, setLoadingSource] = useState(false);
  const [copying, setCopying] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    supplier_type: 'both',
    notes: ''
  });

  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchSuppliers();
    }
  }, [selectedStoreId]);

  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  const fetchSuppliers = async () => {
    if (!selectedStoreId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { store_id: selectedStoreId }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedStoreId) {
      toast.error('Please select a store first');
      return;
    }
    
    // Validate email if provided
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }
    
    setSaving(true);

    try {
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated successfully!');
      } else {
        await axios.post(`${API}/suppliers`, { ...formData, store_id: selectedStoreId });
        toast.success('Supplier created successfully!');
      }
      setDialogOpen(false);
      fetchSuppliers();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save supplier');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      supplier_type: supplier.supplier_type || 'both',
      notes: supplier.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (supplierId, supplierName) => {
    if (!window.confirm(`Are you sure you want to delete "${supplierName}"?`)) return;
    
    try {
      await axios.delete(`${API}/suppliers/${supplierId}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error('Failed to delete supplier');
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      supplier_type: 'both',
      notes: ''
    });
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  // Copy suppliers functions
  const openCopyDialog = () => {
    setSourceStoreId('');
    setDestStoreId('');
    setSourceSuppliers([]);
    setSelectedSupplierIds([]);
    setSkipDuplicates(true);
    setCopyDialogOpen(true);
  };

  const fetchSourceSuppliers = async (storeId) => {
    if (!storeId) {
      setSourceSuppliers([]);
      setSelectedSupplierIds([]);
      return;
    }
    
    setLoadingSource(true);
    try {
      const response = await axios.get(`${API}/suppliers`, {
        params: { store_id: storeId }
      });
      setSourceSuppliers(response.data);
      setSelectedSupplierIds([]); // Reset selection when source changes
    } catch (error) {
      toast.error('Failed to load suppliers from source store');
      setSourceSuppliers([]);
    } finally {
      setLoadingSource(false);
    }
  };

  const handleSourceStoreChange = (storeId) => {
    setSourceStoreId(storeId);
    fetchSourceSuppliers(storeId);
  };

  const toggleSupplierSelection = (supplierId) => {
    setSelectedSupplierIds(prev => 
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSupplierIds.length === sourceSuppliers.length) {
      setSelectedSupplierIds([]);
    } else {
      setSelectedSupplierIds(sourceSuppliers.map(s => s.id));
    }
  };

  const handleCopySuppliers = async () => {
    if (!sourceStoreId || !destStoreId) {
      toast.error('Please select both source and destination stores');
      return;
    }
    
    if (sourceStoreId === destStoreId) {
      toast.error('Source and destination stores must be different');
      return;
    }
    
    if (selectedSupplierIds.length === 0) {
      toast.error('Please select at least one supplier to copy');
      return;
    }
    
    setCopying(true);
    try {
      const response = await axios.post(`${API}/suppliers/copy`, {
        source_store_id: sourceStoreId,
        dest_store_id: destStoreId,
        supplier_ids: selectedSupplierIds,
        skip_duplicates: skipDuplicates
      });
      
      const { copied, skipped } = response.data;
      
      if (copied > 0) {
        toast.success(`${copied} supplier(s) copied successfully${skipped > 0 ? ` (${skipped} duplicates skipped)` : ''}`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} suppliers already exist in destination store`);
      }
      
      setCopyDialogOpen(false);
      
      // Refresh if we're viewing the destination store
      if (selectedStoreId === destStoreId) {
        fetchSuppliers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy suppliers');
    } finally {
      setCopying(false);
    }
  };

  const filteredSuppliers = suppliers.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.contact_person && s.contact_person.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || s.supplier_type === filterType || s.supplier_type === 'both';
    return matchesSearch && matchesType;
  });

  const getTypeLabel = (type) => {
    const found = SUPPLIER_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getTypeBadgeColor = (type) => {
    switch(type) {
      case 'shop': return 'bg-blue-500/20 text-blue-400';
      case 'fuel': return 'bg-orange-500/20 text-orange-400';
      case 'both': return 'bg-green-500/20 text-green-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
            Supplier Management
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage suppliers for shops and fuel stations
          </p>
        </div>

        <div className="flex gap-2">
          {isSuperUser && (
            <Button variant="outline" onClick={openCopyDialog} data-testid="copy-suppliers-btn">
              <Copy className="h-4 w-4 mr-2" />
              Copy Suppliers
            </Button>
          )}
          
          {(isAdmin || isManager) && (
            <Button onClick={openAddDialog} data-testid="add-supplier-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Store Selector */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Store</Label>
              <Select value={selectedStoreId} onValueChange={handleStoreChange}>
                <SelectTrigger data-testid="store-select">
                  <Store className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="relative md:col-span-2">
              <Label className="text-xs text-muted-foreground">&nbsp;</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search suppliers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="supplier-search"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Type Filter</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="supplier-type-filter">
                  <SelectValue placeholder="Filter by Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="shop">Shop Only</SelectItem>
                  <SelectItem value="fuel">Fuel Station Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Suppliers Grid */}
      {!selectedStoreId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Store className="h-12 w-12 mb-4 opacity-50" />
            <p>Please select a store to view suppliers</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : filteredSuppliers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <Truck className="h-12 w-12 mb-4 opacity-50" />
            <p>No suppliers found</p>
            {(isAdmin || isManager) && (
              <Button variant="outline" className="mt-4" onClick={openAddDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Supplier
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSuppliers.map((supplier) => (
            <Card key={supplier.id} className="card-hover" data-testid={`supplier-card-${supplier.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-primary/20">
                      <Truck className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{supplier.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeColor(supplier.supplier_type)}`}>
                        {getTypeLabel(supplier.supplier_type)}
                      </span>
                    </div>
                  </div>
                  
                  {(isAdmin || isManager) && (
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(supplier)}
                        data-testid={`edit-supplier-${supplier.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(supplier.id, supplier.name)}
                          className="text-destructive"
                          data-testid={`delete-supplier-${supplier.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  {supplier.contact_person && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span>{supplier.contact_person}</span>
                    </div>
                  )}
                  {supplier.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{supplier.phone}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span>{supplier.email}</span>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <MapPin className="h-4 w-4" />
                      <span className="truncate">{supplier.address}</span>
                    </div>
                  )}
                </div>

                {supplier.notes && (
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border line-clamp-2">
                    {supplier.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Supplier Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div className="space-y-2">
              <Label>Supplier Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Enter supplier name"
                data-testid="supplier-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <Input
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  placeholder="Contact name"
                  data-testid="supplier-contact-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                  data-testid="supplier-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email address"
                data-testid="supplier-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Full address"
                data-testid="supplier-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={3}
                data-testid="supplier-notes-input"
              />
            </div>
          </form>

          <DialogFooter className="pt-4 border-t border-border mt-4">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving || !formData.name} data-testid="save-supplier-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Suppliers Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy Suppliers Between Stores
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 flex-1 overflow-hidden flex flex-col">
            {/* Store Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Copy FROM (Source Store)</Label>
                <Select value={sourceStoreId} onValueChange={handleSourceStoreChange}>
                  <SelectTrigger data-testid="source-store-select">
                    <SelectValue placeholder="Select source store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Copy TO (Destination Store)</Label>
                <Select value={destStoreId} onValueChange={setDestStoreId}>
                  <SelectTrigger data-testid="dest-store-select">
                    <SelectValue placeholder="Select destination store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.filter(s => s.id !== sourceStoreId).map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Skip Duplicates Option */}
            <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-lg">
              <Checkbox 
                id="skip-duplicates"
                checked={skipDuplicates}
                onCheckedChange={setSkipDuplicates}
              />
              <Label htmlFor="skip-duplicates" className="text-sm cursor-pointer">
                Skip suppliers that already exist in destination store (matched by name)
              </Label>
            </div>

            {/* Supplier List */}
            {sourceStoreId && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">
                    Select Suppliers to Copy ({selectedSupplierIds.length} of {sourceSuppliers.length} selected)
                  </Label>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={toggleSelectAll}
                    disabled={sourceSuppliers.length === 0}
                  >
                    {selectedSupplierIds.length === sourceSuppliers.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </div>
                
                {loadingSource ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : sourceSuppliers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No suppliers found in source store</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-y-auto max-h-[300px]">
                    {sourceSuppliers.map(supplier => (
                      <div 
                        key={supplier.id}
                        className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-muted/50 cursor-pointer ${
                          selectedSupplierIds.includes(supplier.id) ? 'bg-primary/10' : ''
                        }`}
                        onClick={() => toggleSupplierSelection(supplier.id)}
                      >
                        <Checkbox 
                          checked={selectedSupplierIds.includes(supplier.id)}
                          onCheckedChange={() => toggleSupplierSelection(supplier.id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{supplier.name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getTypeBadgeColor(supplier.supplier_type)}`}>
                              {getTypeLabel(supplier.supplier_type)}
                            </span>
                          </div>
                          {supplier.contact_person && (
                            <p className="text-xs text-muted-foreground">{supplier.contact_person}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCopySuppliers} 
              disabled={copying || selectedSupplierIds.length === 0 || !destStoreId}
              data-testid="copy-suppliers-confirm-btn"
            >
              {copying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              Copy {selectedSupplierIds.length} Supplier{selectedSupplierIds.length !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuppliersPage;
